import React from "react";
import { BaseLogo, ProductPrice } from "../../../core/models/entities/BaseLogo";
import { Design } from "../../../core/models/entities/Design";
import { Organization } from "../../../core/models/entities/Organization";
import { CartItemLogo } from "../../screens/order-create/models/CartItem";
import { usePriceVisibility } from "../../../contexts/PriceVisibilityContext";
import { useSalesMarkup } from "../../../hooks/useSalesMarkup";
import { SalesPriceBadge } from "./SalesPriceBadge";

export const getLogoItemQuantity = (item: CartItemLogo) =>
  (item?.toDelivery ?? 0) + (item?.toStorage ?? 0);

export const getPriceByQuantity = (
  prices: ProductPrice[],
  quantity: number
) => {
  const eligible =
    prices.filter((price) => price.minQuantity <= quantity) ?? [];
  return eligible[eligible.length - 1] ?? 0;
};

export const getUniqueSortedPrices = (prices: ProductPrice[]) => {
  if (!prices || !prices.length) return [];
  const uniquePricesMap = new Map<number, ProductPrice>();
  prices.forEach((p) => {
    // Normalize minQuantity: treat 0 and 1 as the same (1)
    let qty = Number(p.minQuantity);
    if (qty === 0) qty = 1;

    // Only set if we haven't seen this quantity yet, or if the current one is preferred
    // (You might want to prefer the one with minQuantity 1 if both 0 and 1 exist, or vice versa.
    // Here we just keep the first one we see, but since we normalize to 1, they collide.)
    if (!uniquePricesMap.has(qty)) {
      uniquePricesMap.set(qty, p);
    }
  });
  return Array.from(uniquePricesMap.values()).sort((a, b) => {
    const qtyA = Number(a.minQuantity) === 0 ? 1 : Number(a.minQuantity);
    const qtyB = Number(b.minQuantity) === 0 ? 1 : Number(b.minQuantity);
    return qtyA - qtyB;
  });
};

export function PricesShort({
  prices,
  skipnLines = 0,
  nLines = 4,
  showBadge = false,
}: {
  prices: ProductPrice[] | undefined;
  skipnLines?: number;
  nLines?: number;
  /**
   * Sales price markup: render the "Sales price" chip inline after the first
   * tier (same line, no extra row). Off by default - the list rows
   * (LogoPrices/DesignPrices) already sit under a column header that says
   * "Sales price", so a chip per row was noise; the popups, which have no
   * header, ask for it.
   */
  showBadge?: boolean;
}) {
  const { showCustomerPrices } = usePriceVisibility();
  // Sales price markup (flag priceMarkupEnabled): inert (identity, no
  // badge) while off; the context forces showCustomerPrices off while on.
  const markup = useSalesMarkup();

  if (!prices || !prices.length) return <></>;
  const sortedPrices = getUniqueSortedPrices(prices);

  let badgePlaced = false;
  return (
    <div className={"flex flex-col"}>
      {sortedPrices.slice(skipnLines, nLines).map((r, i) => {
        // If showing customer prices but adjustedCustomerPrice doesn't exist, don't show price
        if (showCustomerPrices && !r.adjustedCustomerPrice) {
          return null;
        }

        const priceToShow = markup.active
          ? markup.price(r.adjustedPrice, r.adjustedCustomerPrice)
          : showCustomerPrices
            ? r.adjustedCustomerPrice
            : r.adjustedPrice;

        // Skip rendering if customer price is enabled but not available
        if (showCustomerPrices && !r.adjustedCustomerPrice) {
          return null;
        }

        // The chip rides on the first rendered tier's line; null while the
        // markup is inactive, so the flag-off DOM is unchanged.
        const isFirstRenderedTier = !badgePlaced;
        badgePlaced = true;
        const trailing = isFirstRenderedTier ? (
          <>{showBadge && <SalesPriceBadge className="ml-1" />}</>
        ) : undefined;

        return (
          <SimplePriceLine
            key={[r.minQuantity, i].toString()}
            price={priceToShow!}
            quantity={r.minQuantity}
            currency={r.currency}
            trailing={trailing}
          />
        );
      })}
    </div>
  );
}

export function SimplePriceLine({
  price,
  quantity,
  currency,
  trailing,
}: {
  price: number;
  quantity: string | number;
  currency?: string;
  /** Inline content after the amount on the same line (sales price chip). */
  trailing?: React.ReactNode;
}) {
  if (!price) return <></>;
  currency = currency || "DKK";
  const displayQuantity = quantity === 0 ? 1 : quantity;

  return (
    <p className="text-gray-400">
      {currency} {price.toFixed(2)} ({displayQuantity})
      {trailing}
    </p>
  );
}

export function PriceLine({
  price,
  excludeQuantity,
}: {
  price?: ProductPrice;
  excludeQuantity?: boolean;
}) {
  const { showCustomerPrices } = usePriceVisibility();
  const markup = useSalesMarkup();

  if (!price) return <></>;

  let effectivePrice = { ...price };
  if (markup.active) {
    effectivePrice.adjustedPrice = markup.price(
      price.adjustedPrice,
      price.adjustedCustomerPrice,
    );
  } else if (showCustomerPrices) {
    if (
      effectivePrice.adjustedCustomerPrice !== undefined &&
      effectivePrice.adjustedCustomerPrice !== null
    ) {
      effectivePrice.adjustedPrice = effectivePrice.adjustedCustomerPrice;
    } else {
      return <></>;
    }
  }

  return <p>{PriceLineAsString({ price: effectivePrice, excludeQuantity })}</p>;
}

export function PriceTotalLine({
  price,
  quantity,
}: {
  price?: ProductPrice;
  quantity: number;
}): JSX.Element {
  const { showCustomerPrices } = usePriceVisibility();
  const markup = useSalesMarkup();

  if (!price) {
    return <></>;
  }

  let effectivePrice = price.adjustedPrice;
  if (markup.active) {
    effectivePrice = markup.price(
      price.adjustedPrice,
      price.adjustedCustomerPrice,
    );
  } else if (showCustomerPrices) {
    if (
      price.adjustedCustomerPrice !== undefined &&
      price.adjustedCustomerPrice !== null
    ) {
      effectivePrice = price.adjustedCustomerPrice;
    } else {
      return <></>;
    }
  }

  return (
    <>{`${price.currency === "" || !price.currency ? "DKK" : price.currency} ${(
      effectivePrice * quantity
    ).toFixed(2)}`}</>
  );
}

export function PriceLineAsString({
  price,
  excludeQuantity,
}: {
  price?: ProductPrice;
  excludeQuantity?: boolean;
}) {
  if (!price) {
    return "";
  }
  const quantity = excludeQuantity
    ? ""
    : " (" + (price.minQuantity === 0 ? 1 : price.minQuantity) + ")";
  return `${
    price.currency === "" || !price.currency ? "DKK" : price.currency
  } ${price.adjustedPrice.toFixed(2)}${quantity}`;
}

export function PurePriceLine({
  currency,
  price,
  minQuantity,
  excludeQuantity,
}: {
  currency?: string;
  price?: number;
  minQuantity?: number;
  excludeQuantity?: boolean;
}) {
  const markup = useSalesMarkup();
  return (
    <p>
      {PurePriceLineAsString({
        currency,
        price: markup.price(price),
        minQuantity,
        excludeQuantity,
      })}
    </p>
  );
}

export function PurePriceLineAsString({
  currency,
  price,
  minQuantity,
  excludeQuantity,
}: {
  currency?: string;
  price?: number;
  minQuantity?: number;
  excludeQuantity?: boolean;
}): string {
  const quantity = excludeQuantity ? "" : "(" + minQuantity + ")";
  return `${currency === "" || !currency ? "DKK" : currency} ${(
    price ?? 0
  ).toFixed(2)} ${quantity}`;
}

export function LogoPrices({
  logo,
  nLines = 5,
}: {
  logo: BaseLogo;
  nLines?: number;
}) {
  const prices = logo?.product?.prices;
  return <PricesShort prices={prices} nLines={nLines} />;
}

export function DesignPrices({
  design,
  nLines = 5,
}: {
  design: Design;
  nLines?: number;
}) {
  const prices = design.product?.totalPrices;
  return (
    <>
      <PricesShort prices={prices} nLines={nLines} />
      {/* <CustomerSummary customer={design.customer} /> */}
    </>
  );
}

const CustomerSummary = ({ customer }: { customer: Organization }) => (
  <em>
    {customer.name} ({customer.navNumber})
  </em>
);
